import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMobileScreenButton } from "@fortawesome/free-solid-svg-icons";
import { faCommentSms } from "@fortawesome/free-solid-svg-icons";
import { Alert, Button, Container, Header, SpaceBetween, StatusIndicator } from "@cloudscape-design/components";
import { useNotification } from "contexts/NotificationContext";
import PERMISSIONS from "configs/Permissions";
import UserService from "services/UserService";
import userManagementService from "services/userManagementService";

interface SecondFactorStatus {
    authenticator: boolean;
    sms: boolean;
}

type SetupMethod = "authenticator" | "sms";

export default function SecurityTab() {
    const { addNotification } = useNotification();
    const [configuredFactors, setConfiguredFactors] = useState<SecondFactorStatus | null>(null);
    const [isLoadingFactors, setIsLoadingFactors] = useState(true);
    const [statusLoadFailed, setStatusLoadFailed] = useState(false);
    const [startingSetupFor, setStartingSetupFor] = useState<SetupMethod | null>(null);
    const [isRemovingTotp, setIsRemovingTotp] = useState(false);
    const canManageMembers = UserService.hasRole([PERMISSIONS.ORGANIZATIONS.MANAGE_MEMBERS]);

    useEffect(() => {
        let isMounted = true;

        UserService.getConfiguredSecondFactors()
            .then((factors) => {
                if (isMounted) setConfiguredFactors(factors);
            })
            .catch(() => {
                if (isMounted) {
                    setStatusLoadFailed(true);
                    addNotification("error", "Unable to check your configured sign-in methods.");
                }
            })
            .finally(() => {
                if (isMounted) setIsLoadingFactors(false);
            });

        return () => {
            isMounted = false;
        };
    }, [addNotification]);

    const startSetup = async (method: SetupMethod) => {
        setStartingSetupFor(method);
        try {
            if (method === "authenticator") {
                await UserService.activateTotpSetup();
            } else {
                await UserService.activateSmsSetup();
            }
        } catch {
            setStartingSetupFor(null);
            addNotification("error", `Unable to start ${method === "sms" ? "SMS" : "authenticator"} setup. Please try again.`);
        }
    };

    const removeTotp = async () => {
        setIsRemovingTotp(true);
        try {
            await userManagementService.removeTotp();
            setConfiguredFactors((factors) =>
                factors ? { ...factors, authenticator: false } : factors
            );
            setStatusLoadFailed(false);
            addNotification("success", "Authenticator app removed successfully.");
        } catch (error) {
            console.log(error);
            const errorMessage = error instanceof Error ? `: ${error}` : ".";
            addNotification("error", "Unable to remove the authenticator app" + errorMessage);
        } finally {
            setIsRemovingTotp(false);
        }
    };

    if (!canManageMembers) {
        return (
            <Alert type="warning" header="Access restricted">
                Setup signing in methods is restricted to users with the appropriate permissions.
            </Alert>
        );
    }

    return (
        <Container header={<Header variant="h3"> Two-factor authentication</Header>}>
            <SpaceBetween size="m">
                <div>
                    Two-factor authentication adds an additional layer of security to your account by requiring more than just a password to sign in.
                </div>
                {statusLoadFailed && (
                    <Alert type="warning" header="Could not check setup status">
                        You can still start setup, but the configured status may not be current.
                    </Alert>
                )}
                <SpaceBetween size="s">
                    <Container>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", width: "100%" }}>
                            <div style={{ flex:  1 }}>
                               <div>  <FontAwesomeIcon icon={faMobileScreenButton} /> <strong>Authenticator app </strong> 
                               {isLoadingFactors ? (
                                <StatusIndicator type="loading">Checking</StatusIndicator>
                            ) : configuredFactors?.authenticator ? (
                                <StatusIndicator type="success">Configured</StatusIndicator>
                            ) : "" }
                            </div>
                                <div>You will scan a QR code and verify the app on the secure sign-in page.</div>
                            </div>
                            
                            {!configuredFactors?.authenticator ? 
                            <Button
                                variant={configuredFactors?.authenticator ? "normal" : "primary"}
                                onClick={() => startSetup("authenticator")}
                                loading={startingSetupFor === "authenticator"}
                                disabled={isLoadingFactors || startingSetupFor !== null || isRemovingTotp}
                            >
                                Set up
                                
                            </Button>
                            :
                            <Button
                                variant="normal"
                                onClick={removeTotp}
                                loading={isRemovingTotp}
                                disabled={isLoadingFactors || startingSetupFor !== null || isRemovingTotp}
                            >
                                Remove
                            </Button> }
                        </div>
                    </Container>
                    <Container>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", width: "100%" }}>
                            <div style={{ flex: 1 }}>
                                <div>  <FontAwesomeIcon icon={faCommentSms} /> <strong>SMS / Text message</strong> {isLoadingFactors ? (
                                <StatusIndicator type="loading">Checking</StatusIndicator>
                            ) : configuredFactors?.sms ? (
                                <StatusIndicator type="success">Configured</StatusIndicator>
                            ) : ""} </div>
                                <div>Receive a verification code by text message when signing in.</div>
                            </div>

                     {  !configuredFactors?.sms ?
                            <Button
                                variant={configuredFactors?.sms ? "normal" : "primary"}
                                onClick={() => startSetup("sms")}
                                loading={startingSetupFor === "sms"}
                                //disabled={isLoadingFactors || startingSetupFor !== null}
                                disabled
                            >
                               Set up
                            </Button>
                            :
                            <Button
                                variant="normal"
                                //onClick={removeTotp}
                                disabled={startingSetupFor !== null}
                            >
                                Remove
                            </Button>}
                        </div>
                    </Container>
                </SpaceBetween>
            </SpaceBetween>
        </Container>
    );
}
